import Input from '@CS/components/Form/InputField/InputField.module.css';
import { useRef, useState, useEffect } from 'react';
import type { User } from '@/services/api/types';
import type { FormEventHandler } from 'react';
import { useTranslation } from '@CS/hooks';
import Button from '@CS/components/Button';
import { useForm } from 'react-hook-form';
import CSS from './Fields.module.css';

import {
  FIELDS,
  Content,
  InputField,
  SelectField,
  TextareaField
} from '@CS/components/Form';

import type {
  SubmitData,
  // PatternProps,
  InputFieldProps,
  TextAreaProps,
  SelectProps
} from './types';

type FieldsProps = {
  onSubmit: SubmitData;
  isConfirming?: boolean;
  postcodeAPI?: string;
  user?: User;
};

export const Fields = ({
  isConfirming = false,
  postcodeAPI,
  onSubmit,
  user
}: FieldsProps) => {
  const {
    // trigger,
    register,
    setValue,
    // getValues,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const {
    // information,
    termsLabel,
    termsError,
    asteriskAR,
    asteriskEN,
    termsHref,
    infoLabel,
    infoError,
    // address,
    submit,
    link
  } = useTranslation(Content);

  const isSubmitting = useRef(false);
  const startSubmitting = useRef(false);
  const fetchingPostalCode = useRef(false);
  const [terms, setTerms] = useState(false);
  const [info, setInfo] = useState(false);

  /* const postalCodePattern = (
    FIELDS[1].find(({ name }) => name === 'postalCode') as PatternProps
  ).pattern;

  const houseNumberPattern = (
    FIELDS[1].find(({ name }) => name === 'houseNumber') as PatternProps
  ).pattern; */

  const getInputField = ({ last, field, onBlur, disabled }: InputFieldProps) =>
    field.type === 'select' ? (
      <SelectField
        {...field}
        last={last}
        onBlur={onBlur}
        key={field.name}
        disabled={disabled}
        setValue={setValue}
        error={!!errors[field.name]}
        search={(field as SelectProps).search}
        options={(field as SelectProps).options}
        register={register(field.name, {
          required: field.required,
          ...(field.pattern && {
            pattern: field.pattern
          })
        })}
      />
    ) : field.type === 'area' ? (
      <TextareaField
        {...field}
        last={last}
        onBlur={onBlur}
        key={field.name}
        disabled={disabled}
        error={!!errors[field.name]}
        rows={(field as TextAreaProps).rows}
        maxLength={(field as TextAreaProps).maxLength}
        register={register(field.name, {
          required: field.required,
          ...(field.pattern && {
            pattern: field.pattern
          })
        })}
      />
    ) : (
      <InputField
        {...field}
        last={last}
        onBlur={onBlur}
        key={field.name}
        disabled={disabled}
        error={!!errors[field.name]}
        register={register(field.name, {
          required: field.required,
          ...(field.pattern && {
            pattern: field.pattern
          })
        })}
      />
    );

  const onBeforeSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();
    if (!postcodeAPI) return handleSubmit(onSubmit)();

    if (startSubmitting.current) return;
    isSubmitting.current = fetchingPostalCode.current;

    if (!isSubmitting.current) {
      startSubmitting.current = true;
      handleSubmit(onSubmit)();
    }

    return false;
  };

  /* const checkDisabled = (name: string) =>
    !!postcodeAPI && ['streetName', 'venue'].includes(name);

  const getPostalCodeInfo = () => {
    if (!postcodeAPI) return;

    const houseNumberAddition = getValues('suffix');
    const houseNumber = getValues('houseNumber');
    const postcode = getValues('postalCode');

    const params = new URLSearchParams({
      houseNumberAddition,
      houseNumber,
      postcode
    });

    const validPostalCode = postalCodePattern.test(postcode);
    const validHouseNumber = houseNumberPattern.test(houseNumber);
    fetchingPostalCode.current = validPostalCode && validHouseNumber;

    fetchingPostalCode.current &&
      fetch(`${postcodeAPI}?${params}`).then(response =>
        response.json().then(({ street, city }) => {
          fetchingPostalCode.current = false;
          setValue('streetName', street);
          setValue('venue', city);

          if (isSubmitting.current) {
            trigger(['streetName', 'venue']);
            startSubmitting.current = true;
            isSubmitting.current = false;
            handleSubmit(onSubmit)();
          }
        })
      );
  }; */

  useEffect(() => {
    const {
      idNumber,
      lastName,
      firstName,
      nationality,
      phoneNumber,
      emailAddress,
      termsAcknowledged,
      correctInformation
    } = user.data?.formData ?? {};

    setValue('info', correctInformation);
    setValue('terms', termsAcknowledged);
    termsAcknowledged && setTerms(termsAcknowledged);
    correctInformation && setInfo(correctInformation);

    setValue('emailAddress', emailAddress || user.email);
    setValue('firstName', firstName || user.firstname);
    setValue('lastName', lastName || user.lastname);
    setValue('phoneNumber', phoneNumber);
    setValue('nationality', nationality);
    setValue('idNumber', idNumber);
  }, [setValue, user]);

  useEffect(() => {
    startSubmitting.current = isConfirming;
  }, [isConfirming]);

  return (
    <form className={CSS.formContainer} onSubmit={onBeforeSubmit}>
      <div className={CSS.fieldsContainer}>
        {/* <h6 className={CSS.title}>{information}</h6> */}

        {FIELDS[0].map((field, f) =>
          getInputField({
            last: f === FIELDS[0].length - 1,
            field
          })
        )}

        {/* <hr className={CSS.separator} />

        <h6 className={CSS.title}>{address}</h6>

        {FIELDS[1].map((field, f) =>
          getInputField({
            disabled: checkDisabled(field.name),
            last: f === FIELDS[1].length - 1,
            onBlur: getPostalCodeInfo,
            field
          })
        )}

        <hr className={CSS.separator} /> */}
      </div>

      <div className={CSS.infoContainer}>
        <input
          id="info"
          type="checkbox"
          checked={info}
          className={CSS.checkbox}
          {...register('info', { required: true })}
          onChange={event => setInfo(event.target.checked)}
        />

        <label htmlFor="info" className={CSS.infoLabel}>
          {infoLabel}
        </label>

        {errors.info && (
          <em className={Input.fieldError} style={{ marginTop: '10px' }}>
            {infoError}
          </em>
        )}
      </div>

      <div className={CSS.termsContainer}>
        <input
          id="terms"
          type="checkbox"
          checked={terms}
          className={CSS.checkbox}
          {...register('terms', { required: true })}
          onChange={event => setTerms(event.target.checked)}
        />

        <label htmlFor="terms" className={CSS.termsLabel}>
          {termsLabel}&nbsp;
          {asteriskAR}
          <a
            className={CSS.termsLink}
            href={termsHref}
            rel="external"
            id="terms"
          >
            {link}
          </a>
          {asteriskEN}.
        </label>

        {errors.terms && (
          <em className={Input.fieldError} style={{ marginTop: '10px' }}>
            {termsError}
          </em>
        )}
      </div>

      <Button className={CSS.submitButton} text={submit} type="submit" />
    </form>
  );
};
